import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, api, fmtErr, fmtDT, fmtQty, fileUrl } from "../api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "./StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Play, CheckCircle, Plus, Camera, QrCode, AlertCircle } from "lucide-react";

const QC_LABELS = [
  { key: "photo", label: "Lock Photo" },
  { key: "box_weight", label: "Box Weight" },
  { key: "width", label: "Zipper Width" },
  { key: "zipper_weight", label: "Zipper Weight" },
  { key: "joints", label: "Number of Joints" },
];

export function QcChecklist({ box }) {
  const missing = box.qc_missing || [];
  return (
    <div className="space-y-1" data-testid={`qc-checklist-${box.box_number}`}>
      {QC_LABELS.map((f) => {
        const missingThis = missing.includes(f.label);
        return (
          <div key={f.key} className={`flex items-center gap-2 text-sm ${missingThis ? "text-rose-600" : "text-green-700"}`}>
            {missingThis ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {f.label}
          </div>
        );
      })}
    </div>
  );
}

function BoxQCDialog({ open, onClose, box, onSaved }) {
  const [f, setF] = useState({ joints: "", box_weight: "", width: "", zipper_weight: "", remarks: "" });
  const [fullBox, setFullBox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && box) {
      const q = box.qc || {};
      setF({
        joints: q.joints ?? "", box_weight: q.box_weight ?? "", width: q.width ?? "",
        zipper_weight: q.zipper_weight ?? "", remarks: q.remarks || "",
      });
      setFullBox(null);
      api.get(`/boxes/${box.id}`).then((r) => setFullBox(r.data)).catch(() => {});
    }
  }, [open, box]);

  if (!box) return null;
  const photo = fullBox?.qc?.photo || null;
  const hasPhoto = Boolean(photo);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/boxes/${box.id}/qc`, {
        joints: f.joints === "" ? null : parseFloat(f.joints),
        box_weight: f.box_weight === "" ? null : parseFloat(f.box_weight),
        width: f.width || null,
        zipper_weight: f.zipper_weight === "" ? null : parseFloat(f.zipper_weight),
        remarks: f.remarks,
      });
      toast.success("QC saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/boxes/${box.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onSaved();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto" data-testid="box-qc-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">QC — {box.box_id}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="sk-label">Lock / Seal Photo *</label>
            {hasPhoto ? (
              <img src={fileUrl(photo)} alt="Lock / seal" className="w-full rounded border border-border" data-testid="box-qc-photo" />
            ) : (
              <div className="border border-dashed border-rose-300 bg-rose-50 rounded-md px-4 py-4 text-center text-xs text-rose-600" data-testid="box-no-photo">Required — no photo uploaded</div>
            )}
            <label className="mt-2 w-full h-11 rounded-md font-heading font-bold text-sm border border-[#002B5D] text-[#002B5D] inline-flex items-center justify-center gap-2 cursor-pointer">
              <Camera className="w-4 h-4" /> {uploading ? "Uploading..." : hasPhoto ? "Replace Photo" : "Upload / Take Photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="box-qc-photo-input"
                disabled={uploading} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sk-label">Box Weight (Net) *</label>
              <input data-testid="box-qc-box-weight" type="number" step="any" required className="sk-input" value={f.box_weight} onChange={(e) => setF({ ...f, box_weight: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Number of Joints *</label>
              <input data-testid="box-qc-joints" type="number" step="any" required className="sk-input" value={f.joints} onChange={(e) => setF({ ...f, joints: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Zipper Width *</label>
              <input data-testid="box-qc-width" required className="sk-input" placeholder="e.g. 10 mm" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Zipper Weight *</label>
              <input data-testid="box-qc-zipper-weight" type="number" step="any" required className="sk-input" value={f.zipper_weight} onChange={(e) => setF({ ...f, zipper_weight: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="sk-label">Remarks</label>
            <input data-testid="box-qc-remarks" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <button data-testid="box-qc-save" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
            {busy ? "Saving..." : "Save QC"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function BoxesPanel({ mo, onChanged }) {
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [qcBox, setQcBox] = useState(null);
  const [busy, setBusy] = useState(false);
  const canOperate = ["admin", "production"].includes(user?.role);
  const boxes = mo.boxes || [];

  const createBox = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/mos/${mo.id}/boxes`, qty ? { qty: parseFloat(qty) } : {});
      toast.success("Box created");
      setAddOpen(false);
      setQty("");
      onChanged();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="sk-card" data-testid="mo-boxes">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h3 className="font-heading font-bold text-[#002B5D]">Production Boxes</h3>
        {canOperate && mo.lot_id && boxes.length === 0 && !["Completed", "Cancelled"].includes(mo.status) && (
          <button data-testid="add-box-button" onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-md font-heading font-bold text-sm text-white" style={{ backgroundColor: "#002B5D" }}>
            <Plus className="w-4 h-4" /> Add Box
          </button>
        )}
      </div>
      {!mo.lot_id && <div className="px-5 py-6 text-sm text-slate-400 text-center">Generate a Lot — the production box is created automatically.</div>}
      {mo.lot_id && boxes.length === 0 && <div className="px-5 py-6 text-sm text-slate-400 text-center">No box on this lot yet (older order) — click Add Box to create one.</div>}
      {boxes.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full sk-table">
          <thead><tr><th>Box</th><th className="text-right">Qty</th><th>Status</th><th>QC</th><th>Timing</th>{canOperate && <th className="text-right">Actions</th>}</tr></thead>
          <tbody>
            {boxes.map((b) => (
              <tr key={b.id} data-testid={`box-row-${b.box_number}`}>
                <td className="whitespace-normal">
                  <Link to={`/boxes/${b.id}`} className="font-mono font-semibold text-[#002B5D] hover:underline" data-testid={`box-link-${b.box_number}`}>{b.box_number}</Link>
                  {b.operator && <div className="text-[10px] text-slate-400">{b.operator}</div>}
                </td>
                <td className="text-right font-mono">{fmtQty(b.qty)} {b.uom}</td>
                <td><StatusBadge status={b.status} testid={`box-status-${b.box_number}`} /></td>
                <td className="max-w-[110px] whitespace-normal">
                  {b.status === "Completed"
                    ? <StatusBadge status={b.qc?.status || "Passed"} testid={`box-qc-${b.box_number}`} />
                    : (b.qc_missing?.length
                      ? <span className="text-xs text-rose-600 font-semibold block leading-tight" title={`Missing: ${b.qc_missing.join(", ")}`} data-testid={`box-qc-missing-${b.box_number}`}>Missing QC ({b.qc_missing.length})</span>
                      : <span className="text-xs text-green-700 font-semibold" data-testid={`box-qc-ok-${b.box_number}`}>QC complete</span>)}
                </td>
                <td className="whitespace-normal" data-testid={`box-duration-${b.box_number}`}>
                  <div className="text-[11px] font-mono text-slate-500 leading-tight">
                    {b.started_at ? `Start ${fmtDT(b.started_at)}` : "Not started"}
                    {b.completed_at && <><br />End {fmtDT(b.completed_at)}</>}
                  </div>
                  {b.duration_minutes != null && (
                    <div className="text-xs font-mono font-semibold text-[#002B5D]">{Math.floor(b.duration_minutes / 60)}h {Math.round(b.duration_minutes % 60)}m</div>
                  )}
                </td>
                {canOperate && (
                  <td>
                    <div className="flex gap-1 justify-end flex-wrap">
                      {b.status === "Open" && (
                        <button data-testid={`box-start-${b.box_number}`} onClick={() => act(() => api.post(`/boxes/${b.id}/start`), "Production started")}
                          className="h-8 px-3 rounded font-heading font-bold text-xs bg-amber-500 text-white inline-flex items-center gap-1">
                          <Play className="w-3 h-3" /> START
                        </button>
                      )}
                      {b.status !== "Completed" && (
                        <button data-testid={`box-qc-edit-${b.box_number}`} onClick={() => setQcBox(b)}
                          className="h-8 px-3 rounded font-heading font-bold text-xs border border-[#002B5D] text-[#002B5D]">QC</button>
                      )}
                      {b.status === "In Progress" && (
                        <button data-testid={`box-complete-${b.box_number}`} disabled={b.qc_missing?.length > 0}
                          title={b.qc_missing?.length ? `Missing QC: ${b.qc_missing.join(", ")}` : "Complete this box"}
                          onClick={() => act(() => api.post(`/boxes/${b.id}/complete`), "Box completed — inventory updated")}
                          className="h-8 px-3 rounded font-heading font-bold text-xs bg-green-600 text-white inline-flex items-center gap-1 disabled:opacity-40">
                          <CheckCircle className="w-3 h-3" /> COMPLETE
                        </button>
                      )}
                      {b.status === "Completed" && (
                        <Link data-testid={`box-qr-${b.box_number}`} to={`/boxes/${b.id}/label`}
                          className="h-8 px-3 rounded font-heading font-bold text-xs border border-border text-slate-600 inline-flex items-center gap-1">
                          <QrCode className="w-3 h-3" /> Label
                        </Link>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm" data-testid="add-box-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Add Box</DialogTitle></DialogHeader>
          <form onSubmit={createBox} className="space-y-4">
            <div>
              <label className="sk-label">Quantity ({mo.uom})</label>
              <input data-testid="box-qty-input" type="number" step="any" min="0.001" className="sk-input font-mono"
                placeholder={mo.standard_batch_size ? `Default: ${fmtQty(mo.standard_batch_size)}` : ""}
                value={qty} onChange={(e) => setQty(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">Leave empty to use the BOM standard batch size{mo.standard_batch_size ? ` (${fmtQty(mo.standard_batch_size)} ${mo.uom})` : ""}.</p>
            </div>
            <button data-testid="add-box-submit" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#EB1C27" }}>
              {busy ? "Creating..." : "Create Box"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <BoxQCDialog open={Boolean(qcBox)} box={qcBox} onClose={() => setQcBox(null)} onSaved={onChanged} />
    </div>
  );
}
